/**
 * Registro do service worker.
 *
 * Separado do componente para poder ser testado sem navegador de verdade e
 * para deixar explícito o que acontece em cada estado: instalado agora,
 * atualização esperando, atualização aplicada.
 */

/** Chamado quando existe uma versão nova instalada, aguardando permissão. */
export type AoEncontrarAtualizacao = (aplicar: () => void) => void;

let esperando: ServiceWorker | null = null;

/**
 * Registra o service worker, se o navegador tiver suporte.
 *
 * Devolve uma função que cancela a escuta — o React desmonta em modo estrito
 * e registraria os ouvintes duas vezes sem isso.
 */
export function registrarServiceWorker(aoEncontrar: AoEncontrarAtualizacao): () => void {
  // Em desenvolvimento o service worker interceptaria o recarregamento a
  // quente do Vite e faria alterações "não pegarem".
  if (!import.meta.env.PROD) return () => undefined;
  if (!('serviceWorker' in navigator)) return () => undefined;

  let cancelado = false;

  /** Um worker instalado enquanto outro controla a página é uma atualização. */
  const avaliar = (registro: ServiceWorkerRegistration, candidato: ServiceWorker | null) => {
    if (cancelado || !candidato) return;
    if (candidato.state !== 'installed' || !navigator.serviceWorker.controller) return;

    esperando = registro.waiting ?? candidato;
    aoEncontrar(aplicarAtualizacao);
  };

  void navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then((registro) => {
      if (cancelado) return;

      avaliar(registro, registro.waiting);

      registro.addEventListener('updatefound', () => {
        const instalando = registro.installing;
        instalando?.addEventListener('statechange', () => avaliar(registro, instalando));
      });
    })
    .catch(() => {
      // Registro falho não pode derrubar o aplicativo: sem service worker ele
      // funciona igual, só não instala nem abre offline.
    });

  // Quando o worker novo assume, a página recarrega uma vez para passar a ser
  // servida por ele — sem isso, o usuário continuaria na versão antiga até
  // fechar o aplicativo.
  let recarregando = false;
  const aoTrocarControle = () => {
    if (recarregando) return;
    recarregando = true;
    window.location.reload();
  };

  navigator.serviceWorker.addEventListener('controllerchange', aoTrocarControle);

  return () => {
    cancelado = true;
    navigator.serviceWorker.removeEventListener('controllerchange', aoTrocarControle);
  };
}

/** Manda a versão nova assumir. A troca de controle recarrega a página. */
export function aplicarAtualizacao(): void {
  esperando?.postMessage('ATIVAR_AGORA');
}
